/**
 * IONLOOP — full-screen navigation.
 *
 * One shared system for the mobile full-screen menu and the Projects / Solutions
 * panels. Triggers carry `data-ili-nav-panel="<id>"`; panels carry
 * `data-ili-nav-panel-root="<id>"`.
 *
 * Handles: open / close / switch, coordinated body scroll-lock (menu + panels),
 * ESC, focus restore + trap, aria-expanded, and mobile collapse integration.
 * No Bootstrap modal/offcanvas.
 */

(function () {
    'use strict';

    var TRIGGER_SELECTOR = '[data-ili-nav-panel]';
    var ROOT_SELECTOR = '[data-ili-nav-panel-root]';
    var CLOSE_SELECTOR = '[data-ili-nav-panel-close]';
    var ACTIVE_CLASS = 'is-active';
    var OPEN_CLASS = 'is-open';
    var BODY_LOCK_CLASS = 'ili-nav-panel-locked';
    var MOBILE_QUERY = '(max-width: 991.98px)';

    var activePanel = null;
    var lastFocused = null;
    var openedFromMobile = false;

    function closestFrom(target, selector) {
        if (!target || typeof target.closest !== 'function') {
            return null;
        }
        return target.closest(selector);
    }

    function getPanel(id) {
        return document.getElementById('ili-nav-panel-' + id);
    }

    function isMobile() {
        return window.matchMedia(MOBILE_QUERY).matches;
    }

    /* ── Coordinated body scroll lock ─────────────────────────────────── */

    function lockScroll() {
        var scrollbar = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
        document.body.style.setProperty('--ili-nav-scrollbar', scrollbar + 'px');
        document.body.classList.add(BODY_LOCK_CLASS);
    }

    function unlockScroll() {
        document.body.classList.remove(BODY_LOCK_CLASS);
        document.body.style.removeProperty('--ili-nav-scrollbar');
    }

    function shouldLockScroll() {
        if (document.querySelector(ROOT_SELECTOR + '.' + OPEN_CLASS)) {
            return true;
        }
        if (isMobile() && document.querySelector('.ili-nav-collapse.show')) {
            return true;
        }
        return false;
    }

    function updateBodyLock() {
        if (shouldLockScroll()) {
            lockScroll();
        } else {
            unlockScroll();
        }
    }

    function updateTogglerLabel(collapseEl, open) {
        var header = collapseEl.closest ? collapseEl.closest('.ili-enterprise-header') : null;
        var toggler = header ? header.querySelector('.ili-nav-toggler') : null;
        if (toggler) {
            toggler.setAttribute('aria-label', open ? 'Close navigation' : 'Open navigation menu');
        }
    }

    function setTriggersExpanded(id, expanded) {
        var triggers = document.querySelectorAll(TRIGGER_SELECTOR + '[data-ili-nav-panel="' + id + '"]');
        Array.prototype.forEach.call(triggers, function (trigger) {
            trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
            trigger.classList.toggle(ACTIVE_CLASS, expanded);
        });
    }

    function focusable(container) {
        var nodes = container.querySelectorAll(
            'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        return Array.prototype.filter.call(nodes, function (el) {
            return el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement;
        });
    }

    function focusElement(el) {
        if (!el || typeof el.focus !== 'function') {
            return;
        }
        try {
            el.focus({ preventScroll: true });
        } catch (e) {
            try { el.focus(); } catch (e2) { /* ignore */ }
        }
    }

    function openPanel(id, trigger) {
        var panel = getPanel(id);
        if (!panel) {
            return;
        }

        if (activePanel && activePanel !== panel) {
            closePanel({ restoreFocus: false, skipLockUpdate: true });
        }

        lastFocused = trigger || document.activeElement;
        activePanel = panel;
        openedFromMobile = isMobile() && !!closestFrom(trigger, '.ili-nav-collapse');

        panel.classList.add(OPEN_CLASS);
        panel.setAttribute('aria-hidden', 'false');
        setTriggersExpanded(id, true);
        updateBodyLock();

        focusElement(panel.querySelector(CLOSE_SELECTOR) || panel);
    }

    function closePanel(options) {
        options = options || {};
        if (!activePanel) {
            return;
        }

        var panel = activePanel;
        var id = panel.getAttribute('data-ili-nav-panel-root');

        panel.classList.remove(OPEN_CLASS);
        panel.setAttribute('aria-hidden', 'true');
        setTriggersExpanded(id, false);
        activePanel = null;

        if (!options.skipLockUpdate) {
            updateBodyLock();
        }

        if (options.restoreFocus !== false) {
            focusElement(lastFocused);
        }

        lastFocused = null;
        openedFromMobile = false;
    }

    function togglePanel(id, trigger) {
        if (activePanel && activePanel.getAttribute('data-ili-nav-panel-root') === id) {
            closePanel();
        } else {
            openPanel(id, trigger);
        }
    }

    function onDocumentClick(event) {
        var trigger = closestFrom(event.target, TRIGGER_SELECTOR);
        if (trigger) {
            event.preventDefault();
            togglePanel(trigger.getAttribute('data-ili-nav-panel'), trigger);
            return;
        }

        var close = closestFrom(event.target, CLOSE_SELECTOR);
        if (close) {
            event.preventDefault();
            closePanel();
        }
    }

    function onDocumentKeydown(event) {
        if (event.key === 'Escape' || event.key === 'Esc') {
            if (activePanel) {
                event.preventDefault();
                closePanel();
                return;
            }
            var toggler = document.querySelector('.ili-nav-mobile .ili-nav-toggler');
            if (isMobile() && document.querySelector('.ili-nav-collapse.show') && toggler) {
                event.preventDefault();
                toggler.click();
                focusElement(toggler);
            }
            return;
        }

        if (!activePanel || event.key !== 'Tab') {
            return;
        }

        var nodes = focusable(activePanel);
        if (!nodes.length) {
            event.preventDefault();
            return;
        }

        var first = nodes[0];
        var last = nodes[nodes.length - 1];
        var active = document.activeElement;

        if (event.shiftKey) {
            if (active === first || !activePanel.contains(active)) {
                event.preventDefault();
                focusElement(last);
            }
        } else if (active === last) {
            event.preventDefault();
            focusElement(first);
        }
    }

    function onResize() {
        updateBodyLock();
    }

    function init() {
        document.addEventListener('click', onDocumentClick);
        document.addEventListener('keydown', onDocumentKeydown);
        window.addEventListener('resize', onResize);

        var collapses = document.querySelectorAll('.ili-nav-collapse');
        Array.prototype.forEach.call(collapses, function (collapseEl) {
            collapseEl.addEventListener('shown.bs.collapse', function () {
                updateBodyLock();
                updateTogglerLabel(collapseEl, true);
            });
            collapseEl.addEventListener('hidden.bs.collapse', function () {
                if (activePanel && openedFromMobile) {
                    closePanel();
                }
                updateBodyLock();
                updateTogglerLabel(collapseEl, false);
            });
        });
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
